import 'blockly/blocks';
import * as Blockly from 'blockly/core';
import { CATEGORY_BY_ID, type CategoryId } from '../categories';
import type { Registration } from '../registry';

/**
 * Re-registers the whitelisted Blockly built-ins with the studio palette.
 * The original definitions from 'blockly/blocks' are imported first; these
 * JSON overrides replace their colouring (and nothing else — field names,
 * mutators, and semantics stay identical).
 */

const control = CATEGORY_BY_ID.control.colour;
const logic = CATEGORY_BY_ID.logic.colour;
const math = CATEGORY_BY_ID.math.colour;
const text = CATEGORY_BY_ID.text.colour;

/**
 * controls_if keeps Blockly's original definition (and its mutator mixin) and
 * is only restyled. Re-defining it as JSON drops the mutator's
 * loadExtraState, which breaks else / else-if serialization.
 */
const baseControlsIf = (Blockly.Blocks as Record<
  string,
  { init: () => void; __spblockStyled?: boolean } | undefined
>)['controls_if'];
if (baseControlsIf && !baseControlsIf.__spblockStyled) {
  const baseInit = baseControlsIf.init;
  (Blockly.Blocks as Record<string, unknown>)['controls_if'] = {
    __spblockStyled: true,
    init(this: Blockly.Block) {
      baseInit.call(this);
      this.setColour(control);
      this.setTooltip(
        'Run statements depending on a condition. Use the gear icon to add else / else-if branches.',
      );
    },
  };
}

const BUILTIN_STYLES: Record<CategoryId, string> = {
  control,
  gpio: '',
  analog: '',
  interrupts: '',
  time: '',
  power: '',
  comms: '',
  network: '',
  servo: '',
  motor: '',
  display: '',
  lan: '',
  logic,
  math,
  variables: CATEGORY_BY_ID.variables.colour,
  text,
};

export const builtinRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_if_else',
      message0: 'if %1 do %2',
      args0: [
        { type: 'input_value', name: 'IF0', check: 'Boolean' },
        { type: 'input_statement', name: 'DO0' },
      ],
      message1: 'else do %1',
      args1: [{ type: 'input_statement', name: 'ELSE' }],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Run one set of statements when the condition is true, another when it is false.',
    },
    {
      type: 'esp32_if_elseif_else',
      message0: 'if %1 do %2',
      args0: [
        { type: 'input_value', name: 'IF0', check: 'Boolean' },
        { type: 'input_statement', name: 'DO0' },
      ],
      message1: 'else if %1 do %2',
      args1: [
        { type: 'input_value', name: 'IF1', check: 'Boolean' },
        { type: 'input_statement', name: 'DO1' },
      ],
      message2: 'else do %1',
      args2: [{ type: 'input_statement', name: 'ELSE' }],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Check a second condition before falling back to the else branch.',
    },
    {
      type: 'controls_repeat_ext',
      message0: 'repeat %1 times %2',
      args0: [
        { type: 'input_value', name: 'TIMES', check: 'Number' },
        { type: 'input_statement', name: 'DO' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Run the inner statements a number of times.',
    },
    {
      type: 'controls_whileUntil',
      message0: 'repeat %1',
      args0: [
        {
          type: 'field_dropdown',
          name: 'MODE',
          options: [
            ['while', 'WHILE'],
            ['until', 'UNTIL'],
          ],
        },
      ],
      message1: '%1',
      args1: [{ type: 'input_value', name: 'BOOL', check: 'Boolean' }],
      message2: '%1',
      args2: [{ type: 'input_statement', name: 'DO' }],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Run the inner statements while (or until) a condition holds.',
    },
    {
      type: 'controls_for',
      message0: 'count with %1 from %2 to %3 by %4',
      args0: [
        { type: 'field_variable', name: 'VAR', variable: 'i' },
        { type: 'input_value', name: 'FROM', check: 'Number' },
        { type: 'input_value', name: 'TO', check: 'Number' },
        { type: 'input_value', name: 'BY', check: 'Number' },
      ],
      message1: 'do %1',
      args1: [{ type: 'input_statement', name: 'DO' }],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Count from a start value to an end value by a step.',
    },
    {
      type: 'controls_flow_statements',
      message0: '%1',
      args0: [
        {
          type: 'field_dropdown',
          name: 'FLOW',
          options: [
            ['break out of loop', 'BREAK'],
            ['continue with next iteration', 'CONTINUE'],
          ],
        },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.control,
      tooltip: 'Leave the enclosing loop early, or skip to the next iteration.',
    },
    {
      type: 'logic_compare',
      message0: '%1 %2 %3',
      args0: [
        { type: 'input_value', name: 'A' },
        {
          type: 'field_dropdown',
          name: 'OP',
          options: [
            ['=', 'EQ'],
            ['≠', 'NEQ'],
            ['<', 'LT'],
            ['≤', 'LTE'],
            ['>', 'GT'],
            ['≥', 'GTE'],
          ],
        },
        { type: 'input_value', name: 'B' },
      ],
      inputsInline: true,
      output: 'Boolean',
      colour: BUILTIN_STYLES.logic,
      tooltip: 'Compare two values.',
    },
    {
      type: 'logic_operation',
      message0: '%1 %2 %3',
      args0: [
        { type: 'input_value', name: 'A', check: 'Boolean' },
        {
          type: 'field_dropdown',
          name: 'OP',
          options: [
            ['and', 'AND'],
            ['or', 'OR'],
          ],
        },
        { type: 'input_value', name: 'B', check: 'Boolean' },
      ],
      inputsInline: true,
      output: 'Boolean',
      colour: BUILTIN_STYLES.logic,
      tooltip: 'Combine two conditions.',
    },
    {
      type: 'logic_negate',
      message0: 'not %1',
      args0: [{ type: 'input_value', name: 'BOOL', check: 'Boolean' }],
      output: 'Boolean',
      colour: BUILTIN_STYLES.logic,
      tooltip: 'Invert a condition.',
    },
    {
      type: 'logic_boolean',
      message0: '%1',
      args0: [
        {
          type: 'field_dropdown',
          name: 'BOOL',
          options: [
            ['true', 'TRUE'],
            ['false', 'FALSE'],
          ],
        },
      ],
      output: 'Boolean',
      colour: BUILTIN_STYLES.logic,
      tooltip: 'A constant true or false.',
    },
    {
      type: 'math_number',
      message0: '%1',
      args0: [{ type: 'field_number', name: 'NUM' }],
      output: 'Number',
      colour: BUILTIN_STYLES.math,
      tooltip: 'A number.',
    },
    {
      type: 'math_arithmetic',
      message0: '%1 %2 %3',
      args0: [
        { type: 'input_value', name: 'A', check: 'Number' },
        {
          type: 'field_dropdown',
          name: 'OP',
          options: [
            ['+', 'ADD'],
            ['−', 'MINUS'],
            ['×', 'MULTIPLY'],
            ['÷', 'DIVIDE'],
            ['^', 'POWER'],
          ],
        },
        { type: 'input_value', name: 'B', check: 'Number' },
      ],
      inputsInline: true,
      output: 'Number',
      colour: BUILTIN_STYLES.math,
      tooltip: 'Arithmetic on two numbers.',
    },
    {
      type: 'math_modulo',
      message0: 'remainder of %1 ÷ %2',
      args0: [
        { type: 'input_value', name: 'DIVIDEND', check: 'Number' },
        { type: 'input_value', name: 'DIVISOR', check: 'Number' },
      ],
      inputsInline: true,
      output: 'Number',
      colour: BUILTIN_STYLES.math,
      tooltip: 'Integer remainder of a division.',
    },
    {
      type: 'math_random_int',
      message0: 'random integer from %1 to %2',
      args0: [
        { type: 'input_value', name: 'FROM', check: 'Number' },
        { type: 'input_value', name: 'TO', check: 'Number' },
      ],
      inputsInline: true,
      output: 'Number',
      colour: BUILTIN_STYLES.math,
      tooltip: 'Random integer in the range (inclusive).',
    },
    {
      type: 'text',
      message0: '%1',
      args0: [{ type: 'field_input', name: 'TEXT', text: '' }],
      output: 'String',
      colour: BUILTIN_STYLES.text,
      tooltip: 'A piece of text.',
    },
    {
      type: 'variables_get',
      message0: '%1',
      args0: [{ type: 'field_variable', name: 'VAR' }],
      output: null,
      colour: BUILTIN_STYLES.variables,
      tooltip: 'Read a variable.',
    },
    {
      type: 'variables_set',
      message0: 'set %1 to %2',
      args0: [
        { type: 'field_variable', name: 'VAR' },
        { type: 'input_value', name: 'VALUE' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.variables,
      tooltip: 'Assign a value to a variable (type is inferred on first assignment).',
    },
    {
      type: 'math_change',
      message0: 'change %1 by %2',
      args0: [
        { type: 'field_variable', name: 'VAR' },
        { type: 'input_value', name: 'DELTA', check: 'Number' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: BUILTIN_STYLES.variables,
      tooltip: 'Add a delta to a numeric variable.',
    },
  ],
  metas: [
    { type: 'controls_if', category: 'control', label: 'If / do', description: 'Run statements when a condition is true. Use the gear icon to add else / else-if branches.', fields: {}, statementInput: 'DO0' },
    { type: 'esp32_if_else', category: 'control', label: 'If / else', description: 'Run one branch when the condition is true, another when it is false.', fields: { IF0: { kind: 'value', types: 'Boolean' } }, statementInput: 'DO0' },
    { type: 'esp32_if_elseif_else', category: 'control', label: 'If / else if / else', description: 'Check two conditions in order, with a final else branch.', fields: { IF0: { kind: 'value', types: 'Boolean' }, IF1: { kind: 'value', types: 'Boolean' } }, statementInput: 'DO0' },
    { type: 'controls_repeat_ext', category: 'control', label: 'Repeat times', description: 'Repeat the inner statements a number of times.', fields: { TIMES: { kind: 'value', types: 'Number' } }, statementInput: 'DO' },
    { type: 'controls_whileUntil', category: 'control', label: 'Repeat while / until', description: 'Repeat the inner statements while (or until) a condition holds.', fields: { MODE: { kind: 'dropdown', options: ['WHILE', 'UNTIL'] }, BOOL: { kind: 'value', types: 'Boolean' } }, statementInput: 'DO' },
    { type: 'controls_for', category: 'control', label: 'Count with', description: 'Count a variable from a start to an end value by a step.', fields: { VAR: { kind: 'text' }, FROM: { kind: 'value', types: 'Number' }, TO: { kind: 'value', types: 'Number' }, BY: { kind: 'value', types: 'Number' } }, statementInput: 'DO' },
    { type: 'controls_flow_statements', category: 'control', label: 'Break / continue', description: 'Leave the enclosing loop early or skip to the next iteration.', fields: { FLOW: { kind: 'dropdown', options: ['BREAK', 'CONTINUE'] } } },
    { type: 'logic_compare', category: 'logic', label: 'Compare', description: 'Compare two values (=, ≠, <, ≤, >, ≥).', fields: { A: { kind: 'value', types: 'any' }, OP: { kind: 'dropdown', options: ['EQ', 'NEQ', 'LT', 'LTE', 'GT', 'GTE'] }, B: { kind: 'value', types: 'any' } } },
    { type: 'logic_operation', category: 'logic', label: 'And / or', description: 'Combine two boolean values with and / or.', fields: { OP: { kind: 'dropdown', options: ['AND', 'OR'] }, A: { kind: 'value', types: 'Boolean' }, B: { kind: 'value', types: 'Boolean' } } },
    { type: 'logic_negate', category: 'logic', label: 'Not', description: 'Negate a boolean value.', fields: { BOOL: { kind: 'value', types: 'Boolean' } } },
    { type: 'logic_boolean', category: 'logic', label: 'True / false', description: 'A constant boolean value.', fields: { BOOL: { kind: 'dropdown', options: ['TRUE', 'FALSE'] } } },
    { type: 'math_number', category: 'math', label: 'Number', description: 'A numeric constant.', fields: { NUM: { kind: 'number' } } },
    { type: 'math_arithmetic', category: 'math', label: 'Arithmetic', description: 'Add, subtract, multiply, divide or power two numbers.', fields: { OP: { kind: 'dropdown', options: ['ADD', 'MINUS', 'MULTIPLY', 'DIVIDE', 'POWER'] }, A: { kind: 'value', types: 'Number' }, B: { kind: 'value', types: 'Number' } } },
    { type: 'math_modulo', category: 'math', label: 'Remainder', description: 'Remainder of dividing two numbers.', fields: { DIVIDEND: { kind: 'value', types: 'Number' }, DIVISOR: { kind: 'value', types: 'Number' } } },
    { type: 'math_random_int', category: 'math', label: 'Random integer', description: 'A random integer in a range (inclusive).', fields: { FROM: { kind: 'value', types: 'Number' }, TO: { kind: 'value', types: 'Number' } } },
    { type: 'text', category: 'text', label: 'Text', description: 'A text constant.', fields: { TEXT: { kind: 'text' } } },
  ],
};
